// SuperAdminHome.js
import React, { useEffect, useState, useContext } from 'react';
import { UserContext } from "../UserContext";
import Header from '../Header';
import apiClient from '../../api/client';
import ExpiryOverview from '../ExpiryOverview';
import LoadError from '../LoadError';
import { getErrorMessage } from '../../utils/network';
import { formatCount } from '../../utils/expiry';
import { useNavigate } from 'react-router-dom';
import { 
  FaUserShield, 
  FaHeadset, 
  FaUsers, 
  FaWallet, 
  FaChevronRight, 
  FaUserPlus,
  FaBox,
  FaWifi,
  FaEye
} from 'react-icons/fa';
import './index.css';
import AnnouncementModal from '../AnnouncementModal';

const SuperAdminHome = () => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  const [dashboardData, setDashboardData] = useState({
    totalAdmins: 0,
    totalOperators: 0,
    totalCustomers: 0,
    totalWallet: 0,
  });

  const [loading, setLoading] = useState(true);
  // Without this, a failed load (e.g. no internet) showed every card as 0.
  const [dashError, setDashError] = useState(null);
  const [showAnnouncement, setShowAnnouncement] = useState(false);

  // RailWire session summary — same status this shows on the full
  // RailWire Session page, just a compact preview here.
  const [railwireSession, setRailwireSession] = useState(null);
  const [railwireLoading, setRailwireLoading] = useState(true);

  // Activity log preview — most recent entries; how many actually show
  // is controlled by CSS (see index.css) rather than fetching a
  // different count per screen size, so this always fetches a generous
  // 10 and lets the layout hide the rest on smaller screens.
  const [recentActivity, setRecentActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);
  
    // Check if modal should be shown on component mount
    useEffect(() => {
      // Create a unique key for this user's login session      
      const announcementKey = `announcementShown_${user?.operatorId}`;
      
      // Check if we've already shown the announcement for this login session
      const announcementShown = localStorage.getItem(announcementKey);
      
      if (!announcementShown) {
        setShowAnnouncement(true);
        // Mark that we've shown the announcement for this login session
        localStorage.setItem(announcementKey, 'true');
      }
    }, [user]);

  const fetchDashboard = async () => {
    try {
      setDashError(null);
      const { data } = await apiClient.get('/dashboard/superadmin_dashboard.php');
      setDashboardData(data);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch dashboard:', err);
      setDashError(err);
      setLoading(false);
    }
  };

  const fetchRailwireSession = async () => {
    setRailwireLoading(true);
    try {
      const { data } = await apiClient.get('/railwire_session_status.php');
      if (data.success) {
        setRailwireSession(data.session);
      }
    } catch (err) {
      setRailwireSession(null);
    } finally {
      setRailwireLoading(false);
    }
  };

  const fetchRecentActivity = async () => {
    setActivityLoading(true);
    try {
      const { data } = await apiClient.get('/get_activity_log.php?page=1&limit=10');
      if (data.status === 'success') {
        setRecentActivity(data.logs || []);
      }
    } catch (err) {
      setRecentActivity([]);
    } finally {
      setActivityLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
    fetchRailwireSession();
    fetchRecentActivity();
  }, []);

  // Format number with commas
  // Indian grouping (1,23,456) and safe if the API returns nothing.
  const formatNumber = formatCount;

  const handleCloseAnnouncement = () => {
    setShowAnnouncement(false);
  };

  const isRailwireHealthy = railwireSession?.status === 'HEALTHY';

  return (
    <>
      <Header />
      <div className="superadmin-dashboard">

        {/* {showAnnouncement && (
          <AnnouncementModal onClose={handleCloseAnnouncement} />
        )} */}

        <div className="app-link alt-purple">
          <h1>Dashboard</h1>
        </div>

               
        {loading ? ( 
          <div className="dashboard-loading">
            <div className="loader"></div>
            <p>Loading dashboard data...</p>
          </div>
        ) : dashError ? (
          <LoadError
            error={dashError}
            message={getErrorMessage(dashError, 'Could not load the dashboard.')}
            onRetry={() => { setLoading(true); fetchDashboard(); }}
          />
        ) : (
          <>
            <div className="dashboard-cards">
              <div className="card operator-card1" onClick={() => {navigate('/admins')}}>
                <div className="card-icon">
                  <FaUserShield />
                </div>
                <div className="card-content">
                  <h2>{formatNumber(dashboardData.totalAdmins)}</h2>
                  <p>Admins</p>
                </div>
                <div className="card-arrow">
                  <FaChevronRight />
                </div>
              </div>
              
              <div className="card operator-card" onClick={() => {navigate('/operators')}}>
                <div className="card-icon">
                  <FaHeadset />
                </div>
                <div className="card-content">
                  <h2>{formatNumber(dashboardData.totalOperators)}</h2>
                  <p>Operators</p>
                </div>
                <div className="card-arrow">
                  <FaChevronRight />
                </div>
              </div>
              
              <div className="card customer-card" onClick={() => {navigate('/customers')}}>
                <div className="card-icon">
                  <FaUsers />
                </div>
                <div className="card-content">
                  <h2>{formatNumber(dashboardData.totalCustomers)}</h2>
                  <p>Customers</p>
                </div>
                <div className="card-arrow">
                  <FaChevronRight />
                </div>
              </div>
              
              <div className="card wallet-card"> {/* onClick={() => {navigate('/wallet-history')}} */}
                <div className="card-icon">
                  <FaWallet />
                </div>
                <div className="card-content">
                  <h2>₹{formatNumber(dashboardData.totalWallet)}</h2>
                  <p>Total Wallet</p>
                </div>
                <div className="card-arrow">
                  <FaChevronRight />
                </div>
              </div>
            </div>
            
            {/* Quick Actions at the very top, outside the loading state, so they show at once */}
        <div className='quick-actions'>
          <h3>Quick Actions</h3>
          <div className="action-buttons">
            <button className="action-btn admin-btn" onClick={() => navigate('/add-admin')}>
              <FaUserPlus />
              <span>Add Admin</span>
            </button>
            <button className="action-btn operator-btn" onClick={() => navigate('/add-operator')}>
              <FaUserPlus />
              <span>Add Operator</span>
            </button>
            <button className="action-btn customer-btn" onClick={() => navigate('/add-customer')}>
              <FaUserPlus />
              <span>Add Customer</span>
            </button>
            <button className="action-btn assign-btn" onClick={() => navigate('/assign-plans')}>
              <FaBox />
              <span>Assign Packages</span>
            </button>
            <button className="action-btn operator-btn" onClick={() => navigate('/livetv-channels')}>
              <FaBox />
              <span>View Live TV Channels</span>
            </button>
            <button className="action-btn customer-btn" onClick={() => navigate('/ott-content')}>
              <FaBox />
              <span>View OTT Content</span>
            </button>
            <button className="action-btn admin-btn" onClick={() => navigate('/kids-content')}>
              <FaBox />
              <span>View Kids Content</span>
            </button>
            <button className="action-btn assign-btn" onClick={() => navigate('/add-stelfiber-device')}>
              <FaBox />
              <span>StelFiber</span>
            </button>
          </div>
        </div>
        
            <ExpiryOverview />

            {/* RailWire Session + Activity Log — side by side on large
            screens, stacked on medium/small (see .dashboard-widgets
            in index.css). RailWire comes first per request. */}
        <div className="dashboard-widgets">
          <div className="widget-card widget-railwire" onClick={() => navigate('/railwire-session')}>
            <div className="widget-header">
              <span className="widget-title"><FaWifi style={{ marginRight: 8 }} />RailWire Session</span>
              <FaChevronRight className="widget-header-arrow" />
            </div>
            <div className="widget-body">
              {railwireLoading ? (
                <p className="widget-muted">Checking session status…</p>
              ) : !railwireSession ? (
                <p className="widget-muted">Could not reach the RailWire bridge.</p>
              ) : (
                <>
                  <span className={`widget-status-badge ${isRailwireHealthy ? 'healthy' : 'login-required'}`}>
                    {isRailwireHealthy ? 'Session Healthy' : 'Login Required'}
                  </span>
                  <div className="widget-details">
                    {isRailwireHealthy ? (
                      <>
                        <div><strong>Started:</strong> {railwireSession.session_started || '—'}</div>
                        <div><strong>Last activity:</strong> {railwireSession.last_activity || '—'}</div>
                      </>
                    ) : (
                      <div><strong>Logged out at:</strong> {railwireSession.logged_out_at || '—'}</div>
                    )}
                  </div>
                  {!isRailwireHealthy && (
                    <button
                      className="widget-action-btn"
                      onClick={(e) => { e.stopPropagation(); navigate('/railwire-session'); }}
                    >
                      Log back in
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="widget-card widget-activity" onClick={() => navigate('/activity-log')}>
            <div className="widget-header">
              <span className="widget-title"><FaEye style={{ marginRight: 8 }} />Activity Log</span>
              <FaChevronRight className="widget-header-arrow" />
            </div>
            <div className="widget-body">
              {activityLoading ? (
                <p className="widget-muted">Loading recent activity…</p>
              ) : recentActivity.length === 0 ? (
                <p className="widget-muted">No activity yet.</p>
              ) : (
                <ul className="widget-activity-list">
                  {recentActivity.map((log, i) => (
                    <li key={log.id} className={`widget-activity-row row-${i}`}>
                      <span className={`widget-activity-status widget-activity-status--${log.status}`}>
                        {log.status}
                      </span>
                      <span className="widget-activity-action">{log.action}</span>
                      <span className="widget-activity-user">{log.username || '—'}</span>
                      <span className="widget-activity-time">{log.created_at}</span>
                    </li>
                  ))}
                </ul>
              )}
              <button
                className="widget-action-btn"
                onClick={(e) => { e.stopPropagation(); navigate('/activity-log'); }}
              >
                View full log
              </button>
            </div>
          </div>
        </div>


          </>
        )}
      </div>
    </>
  );
};

export default SuperAdminHome;